import { CommonModule } from '@angular/common';
import { Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NzTagComponent } from 'ng-zorro-antd/tag';

/** Characters that split a string into separate chips. Add new delimiters here. */
const CHIP_DELIMITERS = [',', ';'] as const;

@Component({
  selector: 'app-chip-input',
  templateUrl: './chip-input.component.html',
  standalone: true,
  styleUrls: ['./chip-input.component.scss'],
  imports: [CommonModule, FormsModule, NzTagComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipInputComponent),
      multi: true,
    },
  ],
})
export class ChipInputComponent implements ControlValueAccessor {
  @Input() placeholder: string = 'Type and press enter';
  @Input() readonly: boolean = false;

  @ViewChild('chipInput') chipInput!: ElementRef<HTMLInputElement>;

  value: string[] = [];
  inputValue: string = '';
  isDisabled: boolean = false;

  /** Regex matching any delimiter, built from CHIP_DELIMITERS (special chars escaped). */
  private readonly delimiterRegex = new RegExp(
    `[${CHIP_DELIMITERS.map((d) => d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('')}]`,
  );

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(chips: string[] | null): void {
    this.value = Array.isArray(chips) ? [...chips] : [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onBlur(): void {
    this.addChip(this.inputValue);
    this.onTouched();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.isDisabled || this.readonly) {
      return;
    }

    if (event.key === 'Enter' || CHIP_DELIMITERS.includes(event.key as any)) {
      event.preventDefault();
      this.addChip(this.inputValue);
    } else if (event.key === 'Backspace' && !this.inputValue.trim()) {
      this.removeChip(this.value.length - 1);
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (this.isDisabled || this.readonly) {
      return;
    }

    const pastedText = event.clipboardData?.getData('text') ?? '';
    if (this.delimiterRegex.test(pastedText)) {
      event.preventDefault();
      this.addChip(pastedText);
    }
  }

  addChip(value: string): void {
    if (this.isDisabled || this.readonly) {
      return;
    }

    const newChips = value
      .split(this.delimiterRegex)
      .map((chip) => chip.trim())
      .filter((chip) => chip && !this.value.includes(chip));

    if (newChips.length) {
      this.value = [...this.value, ...new Set(newChips)];
      this.onChange(this.value);
    }
    this.inputValue = '';
  }

  removeChip(index: number): void {
    if (this.isDisabled || this.readonly) {
      return;
    }

    if (index >= 0 && index < this.value.length) {
      this.value = this.value.filter((_, itemIndex) => itemIndex !== index);
      this.onChange(this.value);
    }
  }

  focusInput(): void {
    if (!this.isDisabled && !this.readonly) {
      this.chipInput?.nativeElement.focus();
    }
  }
}
